/**
 * Lazy Mixpanel loader — keeps `mixpanel-browser` out of the entry chunk.
 * Callers use withMixpanel() / initMixpanel(); early track() calls queue until init.
 */

let mixpanelInstance = null
let loadPromise = null
let initPromise = null
let inited = false
const pendingOps = []

const isBot = () =>
  typeof navigator !== 'undefined' &&
  !!navigator.userAgent.match(/.*((AhrefsSiteAudit)|(AhrefsBot)).*/i)

const isMixpanelDisabled = () =>
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_MIXPANEL_ENABLED === 'false'

/**
 * Load the Mixpanel SDK (no init). Safe to call repeatedly.
 * @returns {Promise<object|null>}
 */
export function loadMixpanelSdk() {
  if (mixpanelInstance) return Promise.resolve(mixpanelInstance)
  if (loadPromise) return loadPromise
  if (typeof window === 'undefined' || isMixpanelDisabled() || isBot()) {
    return Promise.resolve(null)
  }

  loadPromise = import('mixpanel-browser')
    .then((mod) => {
      mixpanelInstance = mod.default
      return mixpanelInstance
    })
    .catch((err) => {
      console.warn('[mixpanel] failed to load SDK:', err?.message || err)
      loadPromise = null
      return null
    })

  return loadPromise
}

/**
 * @returns {object|null} Sync accessor after load (null if not ready).
 */
export function getMixpanelSync() {
  return inited ? mixpanelInstance : null
}

/**
 * Init Mixpanel once (Session Replay config from initErrorService).
 * @param {string} token
 * @param {object} [config]
 * @returns {Promise<object|null>}
 */
export function initMixpanel(token, config = {}) {
  if (initPromise) return initPromise
  if (!token || isMixpanelDisabled() || isBot()) {
    return Promise.resolve(null)
  }

  initPromise = loadMixpanelSdk().then((mp) => {
    if (!mp) return null
    try {
      if (!inited) {
        mp.init(token, config)
        inited = true
      }
      flushPending(mp)
      return mp
    } catch (err) {
      console.warn('[mixpanel] init failed:', err?.message || err)
      return null
    }
  })

  return initPromise
}

function flushPending(mp) {
  while (pendingOps.length) {
    const op = pendingOps.shift()
    try {
      op(mp)
    } catch (_) {
      // drop failed queued op
    }
  }
}

/**
 * Run `fn(mixpanel)` once Mixpanel is inited. Queues until initMixpanel completes.
 * @param {(mp: object) => void} fn
 */
export async function withMixpanel(fn) {
  if (isMixpanelDisabled() || isBot()) return
  if (inited && mixpanelInstance) {
    fn(mixpanelInstance)
    return
  }
  pendingOps.push(fn)
  // Ensure SDK is loading; initErrorService owns initMixpanel().
  await loadMixpanelSdk()
}

/**
 * @returns {Promise<object|null>}
 */
export async function getMixpanel() {
  if (isMixpanelDisabled() || isBot()) return null
  if (inited && mixpanelInstance) return mixpanelInstance
  return loadMixpanelSdk()
}

export default {
  loadMixpanelSdk,
  initMixpanel,
  getMixpanel,
  getMixpanelSync,
  withMixpanel,
}
